import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GoogleMap, MarkerF, PolylineF } from '@react-google-maps/api';
import { Card, Box, Typography, Button, CircularProgress } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import RouteIcon from '@mui/icons-material/Route';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { onValue } from 'firebase/database';
import { colors } from './Theme/WebTheme';
import { MAIN_COLOR, SECONDORY_COLOR } from '../common/sharedFunctions';

// Live driver tracking for an ACTIVE booking, rendered on the portal's
// Booking Details page so a call-center agent can see what the customer sees
// in the app (BookedCabScreen) — a moving car, pickup/drop pins, the route.
//
// Data path — one tiny RTDB listener and nothing more:
//   * ONE listener on /locations/{driverUid}: the driver's live GPS node
//     (~100 bytes: lat, lng, heading, server-stamped updateTime). Every
//     driver app writes it on each GPS tick, throttled to ≥5 s / ≥25 m with a
//     30 s heartbeat (AppCommon.js → api.saveUserLocation), in the foreground
//     AND from the background location task — for every booking type, with
//     no per-booking gate. It is the same node the server dispatcher and the
//     Dashboard live map trust. Attached only while this card is mounted
//     (active statuses) and the tab is visible. No polling, no /users read,
//     no writes, no Cloud Function.
//     Why not /tracking/{bookingId} (what the customer app subscribes to)?
//     Its continuous pushes are gated on `booking.driverDeviceId === deviceId
//     || booking.manualRequest` (AppCommon.js ~L720); server-dispatched
//     bookings carry driverDeviceId: null, portal-assigned and street/meter
//     trips never push at all, so in production that node is usually a single
//     point or empty — the customer's car icon only moves on legacy flows.
//   * Pickup / drop / waypoints come from the booking object the page already
//     holds — zero extra reads. NOTE: `booking.coords` is NOT the road route;
//     at creation it is just [pickup, ...waypoints, drop] (sharedFunctions
//     ~L202) and at REACHED it is overwritten with the tracked path. The
//     decoded Directions polyline the app draws during the estimate
//     (estimate.waypoints) is never persisted on the booking.
//   * Planned road route (opt-in, "Show route" button): ONE client-side
//     Maps JS DirectionsService request for this booking, cached per booking
//     for the browser session, never auto-fired. It goes browser → Google on
//     the web Maps key, so it adds nothing to RTDB (unlike api.getDirectionsApi,
//     which also writes a log entry to /google_api_logs on every call). The
//     off-route indicator is computed locally against that polyline.
//   * The breadcrumb trail is built locally from positions received while
//     this page is open — no reads.
//
// Only mounted for LIVE_TRACKING_STATUSES; the parent unmounts it (detaching
// the listener) the moment the booking leaves those states.
//
// Collapsed unless the page opens it (expanded / onExpandedChange: Booking
// details opens it as on the approved board and remembers "Hide map"). The
// outer component renders a one-line header built purely from booking data
// the page already has; the inner body (map, RTDB listener, timers,
// Directions button) is mounted only while open and torn down on "Hide map".
// A collapsed card therefore costs nothing — no Dynamic Maps load, no
// listener, no reads; an open one costs one map load and the one listener.

export const LIVE_TRACKING_STATUSES = ['ACCEPTED', 'ARRIVED', 'STARTED'];

const FALLBACK_CAR_ICON = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
const CAR_ICON_MAX_W = 48;
const CAR_ICON_MAX_H = 40;
const PIN_W = 30;
const PIN_H = 42;
const STALE_WARN_MS = 2 * 60 * 1000;
const STALE_ALERT_MS = 5 * 60 * 1000;
const CLOCK_TICK_MS = 5000;
const MAPS_POLL_MS = 300;
const MAPS_POLL_MAX = 60; // ~18 s
// Customer-app camera zoom (BookedCabScreen animateCamera) comes from
// settings.bookingAcceptedZoom / bookingStartedZoom; both default to "map
// default" in the settings catalog, so fall back to a street-level 16.
const DEFAULT_FOLLOW_ZOOM = 16;
const OVERVIEW_MAX_ZOOM = 17;
const FIT_PADDING_PX = 60;
const TRAIL_MAX_POINTS = 120;
const TRAIL_MIN_STEP_KM = 0.005; // 5 m — ignore GPS jitter
const PROGRAMMATIC_GRACE_MS = 1500;
const OFF_ROUTE_M = 150; // GPS error + road width + polyline simplification
const ROUTE_CACHE_MAX = 50;

// Session cache of planned routes keyed by booking id + endpoints, so a
// re-render, re-mount or revisit of the same booking never re-requests.
const ROUTE_CACHE = new Map();

const MAP_OPTIONS = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  clickableIcons: false,
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

const toNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const toLatLng = (p) => {
  if (!p) return null;
  const lat = toNum(p.lat !== undefined ? p.lat : p.latitude);
  const lng = toNum(p.lng !== undefined ? p.lng : p.longitude);
  return lat === null || lng === null ? null : { lat, lng };
};

const toRad = (d) => (d * Math.PI) / 180;
const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

// Metres from `p` to the nearest point on the polyline `path` (segment-
// accurate, flat-earth projection around p — plenty for city distances).
const distanceToPathM = (p, path) => {
  if (!p || !path || path.length === 0) return null;
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.cos(toRad(p.lat));
  const toXY = (q) => ({ x: (q.lng - p.lng) * mPerDegLng, y: (q.lat - p.lat) * mPerDegLat });
  let best = Infinity;
  let prev = toXY(path[0]);
  if (path.length === 1) return Math.hypot(prev.x, prev.y);
  for (let i = 1; i < path.length; i += 1) {
    const cur = toXY(path[i]);
    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    const len2 = dx * dx + dy * dy;
    // Projection of the origin (= p) onto segment prev→cur, clamped.
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(prev.x * dx + prev.y * dy) / len2));
    const d = Math.hypot(prev.x + t * dx, prev.y + t * dy);
    if (d < best) best = d;
    prev = cur;
  }
  return best;
};

// Teardrop pin with a one-letter label, as a data URI so we add no image
// assets and make no network request for marker art.
const pinIcon = (fill, label) => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_W}" height="${PIN_H}" viewBox="0 0 30 42">` +
    `<path d="M15 1C7.3 1 1 7.3 1 15c0 10 14 26 14 26s14-16 14-26C29 7.3 22.7 1 15 1z" fill="${fill}" stroke="#ffffff" stroke-width="2"/>` +
    `<circle cx="15" cy="15" r="8" fill="#ffffff"/>` +
    `<text x="15" y="19.5" font-size="12" font-family="Arial, Helvetica, sans-serif" font-weight="bold" text-anchor="middle" fill="${fill}">${label}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const formatAgo = (ms) => {
  if (ms < 60 * 1000) return `${Math.max(0, Math.round(ms / 1000))}s`;
  if (ms < 60 * 60 * 1000) return `${Math.round(ms / 60000)} min`;
  return `${(ms / 3600000).toFixed(1)} h`;
};

const formatKm = (km, unit) => `${km.toFixed(km < 10 ? 1 : 0)} ${unit}`;

// tr(key, 'English'): the pack's words when the key exists, else the English
// default (i18next defaultValue, the same rule as t(key, { defaultValue })).
const translate = (t) => (key, fallback) => (t ? t(key, { defaultValue: fallback }) : fallback);

const statusLabelFor = (status, tr) => {
  if (status === 'ACCEPTED') return tr('live_tracking_heading_to_pickup', 'Driver heading to pickup');
  if (status === 'ARRIVED') return tr('live_tracking_waiting_at_pickup', 'Driver waiting at pickup');
  return tr('live_tracking_on_trip', 'Trip in progress — heading to drop-off');
};

const HEADER_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  px: 2,
  py: 1.25,
  backgroundColor: SECONDORY_COLOR,
};

const smallButtonStyle = (active) => ({
  textTransform: 'none',
  backgroundColor: active ? MAIN_COLOR : 'transparent',
  color: active ? colors.WHITE : MAIN_COLOR,
  borderColor: MAIN_COLOR,
});

// Everything that costs something lives here; mounted only while expanded.
const LiveTrackingBody = ({ booking, firebase, settings, cars, t, isRTL, onCollapse }) => {
  const tr = useMemo(() => translate(t), [t]);

  const bookingId = booking?.id;
  const driverUid = booking?.driver;
  const status = booking?.status;
  // settings.portalLiveTrackingRoute gates the only part of this panel that
  // can bill Maps beyond the map load (one Directions request per booking).
  const routeFeatureOn = settings?.portalLiveTrackingRoute === true;

  const [map, setMap] = useState(null);
  const [mapsReady, setMapsReady] = useState(() => !!(window.google && window.google.maps));
  // Fixed at mount on purpose: the booking object is re-created on every RTDB
  // update, and a derived `center` prop would yank the map back under the
  // agent each time. Framing after mount is handled by the follow/overview code.
  const [initialCenter] = useState(
    () => toLatLng(booking?.pickup) || toLatLng(booking?.drop) || { lat: 9.0108, lng: 38.7613 } // Addis Ababa
  );
  const [driverLoc, setDriverLoc] = useState(null);
  const [trail, setTrail] = useState([]);
  const [trackingError, setTrackingError] = useState(false);
  // Default view is the overview (car + pins in frame); the agent opts into
  // the zoomed follow camera.
  const [follow, setFollow] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [carIconSize, setCarIconSize] = useState({ w: CAR_ICON_MAX_W, h: 36 });
  // Planned road route: null until the agent asks for it.
  const [route, setRoute] = useState(null);
  const [routeState, setRouteState] = useState('idle'); // idle | loading | ready | denied | error
  const [routeVisible, setRouteVisible] = useState(false);

  // Camera moves we trigger ourselves (panTo / setZoom / fitBounds) also fire
  // zoom_changed; these refs let us tell them apart from the agent zooming,
  // which should switch follow mode off just like a drag does.
  const programmaticRef = useRef(false);
  const programmaticTimerRef = useRef(null);
  const hasFramedRef = useRef(false);
  const followZoomAppliedRef = useRef(null); // zoom we last applied for follow
  // Initial overview framing: 0 = not yet, 1 = framed without a car fix,
  // 2 = framed with the car. Once the agent drags/zooms we stop re-framing.
  const overviewStageRef = useRef(0);
  const userInteractedRef = useRef(false);

  // The Maps JS SDK is loaded once by App.js (useJsApiLoader). On a direct
  // deep-link into this page it may still be in flight, so wait for it
  // instead of letting <GoogleMap> throw on a missing `google` global.
  useEffect(() => {
    if (mapsReady) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(timer);
      } else if (tries >= MAPS_POLL_MAX) {
        clearInterval(timer);
      }
    }, MAPS_POLL_MS);
    return () => clearInterval(timer);
  }, [mapsReady]);

  // Live driver position: the driver's own /locations/{uid} node. The
  // listener is attached only while this card is mounted (active statuses),
  // only for this one driver, and only while the browser tab is visible —
  // a tab left open in the background detaches and re-attaches on return
  // (one ~100-byte snapshot), so forgotten tabs stream nothing.
  useEffect(() => {
    if (!firebase || !driverUid) return undefined;
    setDriverLoc(null);
    setTrail([]);
    setTrackingError(false);

    let unsubscribe = null;
    const attach = () => {
      if (unsubscribe) return;
      unsubscribe = onValue(
        firebase.userLocationRef(driverUid),
        (snapshot) => {
          const val = snapshot.val();
          const pos = toLatLng(val);
          if (!pos) return;
          setDriverLoc({
            ...pos,
            heading: toNum(val.heading),
            // Server-stamped by saveUserLocation → staleness is immune to the
            // driver phone's clock. Falls back to receipt time if ever missing.
            at: toNum(val.updateTime) || Date.now(),
          });
          // Breadcrumb trail of where the car has been since the page opened.
          setTrail((prev) => {
            const last = prev.length ? prev[prev.length - 1] : null;
            if (last && haversineKm(last, pos) < TRAIL_MIN_STEP_KM) return prev;
            const next = prev.concat([pos]);
            return next.length > TRAIL_MAX_POINTS ? next.slice(next.length - TRAIL_MAX_POINTS) : next;
          });
        },
        (error) => {
          console.error('[LiveTrackingMap] driver location subscription failed:', error);
          setTrackingError(true);
        }
      );
    };
    const detach = () => {
      if (!unsubscribe) return;
      try { unsubscribe(); } catch (_) { /* already detached */ }
      unsubscribe = null;
    };
    const onVisibility = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') attach();
      else detach();
    };

    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      detach();
    };
  }, [firebase, driverUid]);

  // Clock for the "updated Xs ago" label. Cheap local re-render only.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const pickup = useMemo(() => toLatLng(booking?.pickup), [booking?.pickup]);
  const drop = useMemo(() => toLatLng(booking?.drop), [booking?.drop]);
  const waypoints = useMemo(
    () => (Array.isArray(booking?.waypoints) ? booking.waypoints.map(toLatLng).filter(Boolean) : []),
    [booking?.waypoints]
  );

  const target = status === 'STARTED' ? drop : pickup;

  const followZoom = useMemo(() => {
    const raw = status === 'STARTED' ? settings?.bookingStartedZoom : settings?.bookingAcceptedZoom;
    const z = toNum(raw);
    return z !== null && z >= 10 && z <= 20 ? z : DEFAULT_FOLLOW_ZOOM;
  }, [settings?.bookingStartedZoom, settings?.bookingAcceptedZoom, status]);

  const carIcon = useMemo(() => {
    const byType = Array.isArray(cars) ? cars.find((c) => c && c.name === booking?.carType) : null;
    return (byType && byType.image) || booking?.car_image || FALLBACK_CAR_ICON;
  }, [cars, booking?.carType, booking?.car_image]);

  // Car-type images are wide (side view); size the marker to the image's own
  // aspect ratio instead of forcing a square, which squashed/stretched it.
  useEffect(() => {
    if (!carIcon) return undefined;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled || !img.naturalWidth || !img.naturalHeight) return;
      const ratio = img.naturalHeight / img.naturalWidth;
      let w = CAR_ICON_MAX_W;
      let h = Math.round(w * ratio);
      if (h > CAR_ICON_MAX_H) {
        h = CAR_ICON_MAX_H;
        w = Math.round(h / ratio);
      }
      setCarIconSize({ w, h });
    };
    img.src = carIcon;
    return () => { cancelled = true; };
  }, [carIcon]);

  // ── Camera helpers ──────────────────────────────────────────────────────
  const markProgrammatic = useCallback(() => {
    programmaticRef.current = true;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    // Safety net: if the camera didn't actually change, no `idle` fires, so
    // release the flag on a timer too.
    programmaticTimerRef.current = setTimeout(() => {
      programmaticRef.current = false;
    }, PROGRAMMATIC_GRACE_MS);
  }, []);

  useEffect(() => () => {
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
  }, []);

  const overviewPoints = useMemo(() => {
    const pts = [];
    if (driverLoc) pts.push(driverLoc);
    if (pickup) pts.push(pickup);
    waypoints.forEach((w) => pts.push(w));
    if (drop) pts.push(drop);
    return pts;
  }, [driverLoc, pickup, drop, waypoints]);

  // Overview: everything in frame (car, pins, and the route if shown).
  const fitOverview = useCallback(() => {
    if (!map || !window.google || !window.google.maps) return;
    const pts = routeFeatureOn && routeVisible && route ? overviewPoints.concat(route.path) : overviewPoints;
    if (pts.length === 0) return;
    markProgrammatic();
    hasFramedRef.current = true;
    followZoomAppliedRef.current = null;
    if (pts.length === 1) {
      map.setCenter(pts[0]);
      map.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    pts.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, FIT_PADDING_PX);
  }, [map, overviewPoints, routeFeatureOn, routeVisible, route, markProgrammatic]);

  // Follow: the customer-app camera — centred on the car at the configured
  // zoom (BookedCabScreen animateCamera), re-centred on every position
  // update. Until the first GPS fix arrives, show the overview instead.
  useEffect(() => {
    if (!map || !follow) return;
    if (!driverLoc) {
      if (!hasFramedRef.current) fitOverview();
      return;
    }
    markProgrammatic();
    hasFramedRef.current = true;
    if (followZoomAppliedRef.current !== followZoom) {
      followZoomAppliedRef.current = followZoom;
      map.setZoom(followZoom);
    }
    map.panTo({ lat: driverLoc.lat, lng: driverLoc.lng });
  }, [map, follow, driverLoc, followZoom, fitOverview, markProgrammatic]);

  // Default (overview) framing: once on load, and once more when the first
  // car fix arrives so the car is in frame too — unless the agent has
  // already taken control of the camera.
  useEffect(() => {
    if (!map || follow || userInteractedRef.current) return;
    const stage = driverLoc ? 2 : 1;
    if (overviewStageRef.current >= stage) return;
    overviewStageRef.current = stage;
    fitOverview();
  }, [map, follow, driverLoc, fitOverview]);

  const handleMapLoad = useCallback((instance) => {
    hasFramedRef.current = false;
    followZoomAppliedRef.current = null;
    overviewStageRef.current = 0;
    userInteractedRef.current = false;
    setMap(instance);
  }, []);

  const handleMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleIdle = useCallback(() => {
    if (!map || !programmaticRef.current) return;
    // fitBounds on a car 80 m from its pin would zoom to rooftop level;
    // clamp, which triggers one more (still programmatic) zoom + idle cycle.
    const zoom = map.getZoom ? map.getZoom() : null;
    if (followZoomAppliedRef.current === null && zoom !== null && zoom > OVERVIEW_MAX_ZOOM) {
      markProgrammatic();
      map.setZoom(OVERVIEW_MAX_ZOOM);
      return;
    }
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticRef.current = false;
  }, [map, markProgrammatic]);

  const stopFollowing = useCallback(() => {
    followZoomAppliedRef.current = null;
    setFollow((f) => (f ? false : f));
  }, []);

  const handleDragStart = useCallback(() => {
    userInteractedRef.current = true;
    stopFollowing();
  }, [stopFollowing]);

  const handleZoomChanged = useCallback(() => {
    // Only the agent's own zoom (wheel / +− buttons) should break follow.
    if (programmaticRef.current || !hasFramedRef.current) return;
    userInteractedRef.current = true;
    stopFollowing();
  }, [stopFollowing]);

  const handleFollow = useCallback(() => {
    followZoomAppliedRef.current = null; // re-apply zoom when turning on
    setFollow(true);
  }, []);

  const handleOverview = useCallback(() => {
    userInteractedRef.current = false;
    overviewStageRef.current = driverLoc ? 2 : 1;
    stopFollowing();
    fitOverview();
  }, [stopFollowing, fitOverview, driverLoc]);

  // ── Planned road route (opt-in) ─────────────────────────────────────────
  const routeCacheKey = useMemo(() => {
    if (!bookingId || !pickup || !drop) return null;
    const wp = waypoints.map((w) => `${w.lat},${w.lng}`).join('|');
    return `${bookingId}|${pickup.lat},${pickup.lng}|${drop.lat},${drop.lng}|${wp}`;
  }, [bookingId, pickup, drop, waypoints]);

  const requestRoute = useCallback(() => {
    if (!routeCacheKey || !window.google || !window.google.maps || !window.google.maps.DirectionsService) {
      setRouteState('error');
      return;
    }
    const cached = ROUTE_CACHE.get(routeCacheKey);
    if (cached) {
      setRoute(cached);
      setRouteState('ready');
      setRouteVisible(true);
      return;
    }
    setRouteState('loading');
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: pickup,
        destination: drop,
        waypoints: waypoints.map((w) => ({ location: w, stopover: true })),
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
      },
      (result, resultStatus) => {
        const first = result && result.routes && result.routes[0];
        if (resultStatus === 'OK' && first) {
          // Full step geometry (not overview_path) so the off-route check
          // doesn't flag a car halfway down a long simplified segment.
          const path = [];
          let distanceM = 0;
          let durationS = 0;
          (first.legs || []).forEach((leg) => {
            distanceM += (leg.distance && leg.distance.value) || 0;
            durationS += (leg.duration && leg.duration.value) || 0;
            (leg.steps || []).forEach((step) => {
              (step.path || []).forEach((pt) => path.push({ lat: pt.lat(), lng: pt.lng() }));
            });
          });
          if (path.length < 2 && first.overview_path) {
            first.overview_path.forEach((pt) => path.push({ lat: pt.lat(), lng: pt.lng() }));
          }
          const built = { path, distanceKm: distanceM / 1000, durationMin: durationS / 60 };
          if (ROUTE_CACHE.size >= ROUTE_CACHE_MAX) {
            ROUTE_CACHE.delete(ROUTE_CACHE.keys().next().value);
          }
          ROUTE_CACHE.set(routeCacheKey, built);
          setRoute(built);
          setRouteState('ready');
          setRouteVisible(true);
        } else {
          console.error('[LiveTrackingMap] DirectionsService failed:', resultStatus);
          setRouteState(resultStatus === 'REQUEST_DENIED' ? 'denied' : 'error');
        }
      }
    );
  }, [routeCacheKey, pickup, drop, waypoints]);

  const handleRouteToggle = useCallback(() => {
    if (routeVisible) {
      setRouteVisible(false);
      return;
    }
    if (route && routeState === 'ready') {
      setRouteVisible(true);
      return;
    }
    requestRoute();
  }, [routeVisible, route, routeState, requestRoute]);

  // Off-route check, local arithmetic only, once a route is loaded.
  const offRouteM = useMemo(() => {
    if (!routeFeatureOn || !route || !driverLoc || !route.path || route.path.length < 2) return null;
    return distanceToPathM(driverLoc, route.path);
  }, [routeFeatureOn, route, driverLoc]);

  // ── Labels ──────────────────────────────────────────────────────────────
  const statusLabel = statusLabelFor(status, tr);

  const ageMs = driverLoc && driverLoc.at ? Math.max(0, now - driverLoc.at) : null;
  const ageColor =
    ageMs === null ? '#666' : ageMs >= STALE_ALERT_MS ? colors.RED : ageMs >= STALE_WARN_MS ? '#E07B00' : colors.GREEN;
  const distanceKm = driverLoc && target ? haversineKm(driverLoc, target) : null;
  const distanceLabel = status === 'STARTED'
    ? tr('live_tracking_to_dropoff', 'to drop-off')
    : tr('live_tracking_to_pickup', 'to pickup');
  const kmUnit = tr('km', 'km');

  const routeButtonLabel = routeState === 'loading'
    ? tr('live_tracking_route_loading', 'Loading route…')
    : routeVisible
      ? tr('live_tracking_hide_route', 'Hide route')
      : tr('live_tracking_show_route', 'Show route');

  const routeNote = (() => {
    if (routeState === 'denied') return tr('live_tracking_route_denied', 'Route unavailable: Directions API is not enabled for the web Maps key.');
    if (routeState === 'error') return tr('live_tracking_route_error', 'Route unavailable right now.');
    return null;
  })();

  const canUseGoogleSize = mapsReady && window.google && window.google.maps;

  return (
    <>
      <Box sx={{ ...HEADER_SX, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: ageColor,
              boxShadow: `0 0 0 3px ${ageColor}33`,
              flexShrink: 0,
            }}
          />
          <Typography style={{ fontWeight: 'bold', fontSize: 16, color: colors.BLACK }}>
            {tr('live_tracking', 'Live tracking')}
          </Typography>
          <Typography style={{ fontSize: 14, color: '#333' }}>
            {statusLabel}
          </Typography>
          {offRouteM !== null ? (
            <Typography
              style={{
                fontSize: 13,
                fontWeight: 'bold',
                color: offRouteM > OFF_ROUTE_M ? colors.WHITE : colors.GREEN,
                backgroundColor: offRouteM > OFF_ROUTE_M ? colors.RED : `${colors.GREEN}22`,
                borderRadius: 12,
                padding: '2px 10px',
              }}
            >
              {offRouteM > OFF_ROUTE_M
                ? `${tr('live_tracking_off_route', 'Off route by')} ${offRouteM >= 1000 ? `${(offRouteM / 1000).toFixed(1)} ${kmUnit}` : `${Math.round(offRouteM)} m`}`
                : tr('live_tracking_on_route', 'On planned route')}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {distanceKm !== null ? (
            <Typography style={{ fontSize: 14, color: '#333' }}>
              {`${formatKm(distanceKm, kmUnit)} ${distanceLabel}`}
              <span style={{ color: '#777' }}>{` (${tr('live_tracking_straight_line', 'straight line')})`}</span>
            </Typography>
          ) : null}
          {routeFeatureOn && route && routeVisible ? (
            <Typography style={{ fontSize: 14, color: '#333' }}>
              {`${tr('live_tracking_planned', 'Planned')}: ${formatKm(route.distanceKm, kmUnit)} · ${Math.round(route.durationMin)} ${tr('minutes', 'min')}`}
            </Typography>
          ) : null}
          <Typography style={{ fontSize: 14, color: ageColor, fontWeight: ageMs !== null && ageMs >= STALE_WARN_MS ? 'bold' : 'normal' }}>
            {ageMs === null
              ? trackingError
                ? tr('live_tracking_unavailable', 'Location unavailable')
                : tr('live_tracking_waiting_fix', 'Waiting for driver GPS…')
              : ageMs >= STALE_ALERT_MS
                ? `${tr('live_tracking_no_update', 'No position update for')} ${formatAgo(ageMs)}`
                : `${tr('live_tracking_updated', 'Updated')} ${formatAgo(ageMs)} ${tr('ago', 'ago')}`}
          </Typography>
          <Button
            size="small"
            variant={!follow ? 'contained' : 'outlined'}
            startIcon={<ZoomOutMapIcon fontSize="small" />}
            onClick={handleOverview}
            title={tr('live_tracking_overview_hint', 'Fit the car, pickup, drop-off and route in view.')}
            style={smallButtonStyle(!follow)}
          >
            {tr('live_tracking_overview', 'Overview')}
          </Button>
          <Button
            size="small"
            variant={follow ? 'contained' : 'outlined'}
            startIcon={<MyLocationIcon fontSize="small" />}
            onClick={handleFollow}
            title={follow
              ? tr('live_tracking_following_hint', 'Camera stays on the car at street zoom, like the customer app. Drag or zoom to stop.')
              : tr('live_tracking_follow_hint', 'Centre on the car at street zoom and keep following it.')}
            style={smallButtonStyle(follow)}
          >
            {follow ? tr('live_tracking_following', 'Following') : tr('live_tracking_follow', 'Follow driver')}
          </Button>
          {routeFeatureOn ? (
          <Button
            size="small"
            variant={routeVisible ? 'contained' : 'outlined'}
            disabled={routeState === 'loading' || !routeCacheKey}
            startIcon={routeState === 'loading' ? <CircularProgress size={14} color="inherit" /> : <RouteIcon fontSize="small" />}
            onClick={handleRouteToggle}
            title={tr('live_tracking_route_hint', 'Draw the planned pickup → drop-off road route (one Directions request per booking) to see if the driver is off course.')}
            style={smallButtonStyle(routeVisible)}
          >
            {routeButtonLabel}
          </Button>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ExpandLessIcon fontSize="small" />}
            onClick={onCollapse}
            title={tr('live_tracking_hide_hint', 'Hide the map and stop listening for the driver position.')}
            style={smallButtonStyle(false)}
          >
            {tr('live_tracking_hide_map', 'Hide map')}
          </Button>
        </Box>
        {routeFeatureOn && routeNote ? (
          <Typography style={{ fontSize: 13, color: colors.RED, width: '100%' }}>
            {routeNote}
          </Typography>
        ) : null}
      </Box>

      {mapsReady ? (
        <GoogleMap
          mapContainerStyle={{ height: 420, width: '100%' }}
          center={initialCenter}
          zoom={14}
          options={MAP_OPTIONS}
          onLoad={handleMapLoad}
          onUnmount={handleMapUnmount}
          onDragStart={handleDragStart}
          onZoomChanged={handleZoomChanged}
          onIdle={handleIdle}
        >
          {routeFeatureOn && routeVisible && route && route.path.length > 1 ? (
            <PolylineF
              path={route.path}
              options={{ strokeColor: '#1E88E5', strokeOpacity: 0.85, strokeWeight: 5, zIndex: 1 }}
            />
          ) : null}
          {trail.length > 1 ? (
            <PolylineF
              path={trail}
              options={{ strokeColor: MAIN_COLOR, strokeOpacity: 0.9, strokeWeight: 3, zIndex: 2 }}
            />
          ) : null}
          {pickup ? (
            <MarkerF
              position={pickup}
              title={booking?.pickupAddress || booking?.pickup?.add || tr('pickup_location', 'Pickup Location')}
              zIndex={3}
              icon={canUseGoogleSize ? {
                url: pinIcon(colors.GREEN, 'P'),
                scaledSize: new window.google.maps.Size(PIN_W, PIN_H),
                anchor: new window.google.maps.Point(PIN_W / 2, PIN_H),
              } : undefined}
            />
          ) : null}
          {waypoints.map((w, i) => (
            <MarkerF
              key={`wp-${i}`}
              position={w}
              title={(booking?.waypoints && booking.waypoints[i] && booking.waypoints[i].add) || `${tr('waypoint', 'Stop')} ${i + 1}`}
              zIndex={3}
              icon={canUseGoogleSize ? {
                url: pinIcon('#F9A825', String(i + 1)),
                scaledSize: new window.google.maps.Size(PIN_W, PIN_H),
                anchor: new window.google.maps.Point(PIN_W / 2, PIN_H),
              } : undefined}
            />
          ))}
          {drop ? (
            <MarkerF
              position={drop}
              title={booking?.dropAddress || booking?.drop?.add || tr('drop_location', 'Drop Location')}
              zIndex={3}
              icon={canUseGoogleSize ? {
                url: pinIcon(colors.RED, 'D'),
                scaledSize: new window.google.maps.Size(PIN_W, PIN_H),
                anchor: new window.google.maps.Point(PIN_W / 2, PIN_H),
              } : undefined}
            />
          ) : null}
          {driverLoc ? (
            <MarkerF
              position={{ lat: driverLoc.lat, lng: driverLoc.lng }}
              title={[booking?.driver_name, booking?.vehicle_number].filter(Boolean).join(' · ') || tr('driver', 'Driver')}
              zIndex={4}
              icon={canUseGoogleSize ? {
                url: carIcon,
                scaledSize: new window.google.maps.Size(carIconSize.w, carIconSize.h),
                anchor: new window.google.maps.Point(carIconSize.w / 2, carIconSize.h / 2),
              } : undefined}
            />
          ) : null}
        </GoogleMap>
      ) : (
        <Box sx={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography style={{ color: '#666' }}>
            {tr('loading_map', 'Loading map…')}
          </Typography>
        </Box>
      )}
    </>
  );
};

// Outer shell: zero cost while collapsed. Open or closed is the page's call
// when it passes `expanded` (with onExpandedChange); otherwise it starts closed.
const LiveTrackingMap = (props) => {
  const { booking, t, isRTL, expanded: expandedProp, onExpandedChange } = props;
  const [expandedState, setExpandedState] = useState(false);
  const controlled = typeof expandedProp === 'boolean';
  const expanded = controlled ? expandedProp : expandedState;
  const setExpanded = (open) => {
    if (onExpandedChange) onExpandedChange(open);
    if (!controlled) setExpandedState(open);
  };
  const tr = useMemo(() => translate(t), [t]);

  return (
    <Card
      style={{
        borderRadius: 10,
        backgroundColor: colors.WHITE,
        marginTop: 5,
        marginBottom: 20,
        boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
        overflow: 'hidden',
      }}
    >
      {expanded ? (
        <LiveTrackingBody {...props} onCollapse={() => setExpanded(false)} />
      ) : (
        <Box sx={{ ...HEADER_SX, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flexWrap: 'wrap' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9e9e9e', flexShrink: 0 }} />
            <Typography style={{ fontWeight: 'bold', fontSize: 16, color: colors.BLACK }}>
              {tr('live_tracking', 'Live tracking')}
            </Typography>
            <Typography style={{ fontSize: 14, color: '#333' }}>
              {statusLabelFor(booking?.status, tr)}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<ExpandMoreIcon fontSize="small" />}
            onClick={() => setExpanded(true)}
            title={tr('live_tracking_show_hint', 'Load the map and start following the driver position for this booking.')}
            style={smallButtonStyle(true)}
          >
            {tr('live_tracking_show_map', 'Show map')}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default LiveTrackingMap;
